// src/admin/companies/CompanyPurgeDialog.jsx — the company purge (v212). Decisions:
// C3 no mode preselected: two radios (close first); the ERASE preview loads in the background on open (the "probe")
//    and greys «erase» while it loads or when it names ERASE_REFUSED_LIVE_DATA / PURGE_MODE_MISMATCH. «close»
//    loads its own preview only once chosen. Mode = the radio, or — `resume` (C5) — the mode the purge started in.
// C6 the purge is one synchronous request: busy line; no status or a 504 = «non sappiamo se è terminata».
// The typed id is compared case-insensitively and the CANONICAL id is sent (the backend wants it lowercase).
// Gone (CUSTOMER_NOT_FOUND on the probe or the purge) → onClose({ gone: true }): the page leaves for the list.
// ⛔ Never server text: blockers via adminErrorCopy (TENANT_PROTECTED and the no-Stripe-key case by our sentences).
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TypedConfirmDialog from '../purge/TypedConfirmDialog.jsx';
import { CompanyPurgeSummary } from '../purge/PurgeSummaries.jsx';
import { usePurgePreview } from '../purge/usePurgePreview.js';
import { PURGE_MODES, previewCompanyPurge, purgeCompany } from '../companiesApi.js';
import { companyBlocker, eraseUnavailableKey, startedMode } from './companyModel.js';
import { adminErrorText, isGoneError } from '../adminErrorCopy.js';
import { COLORS, FONT_SANS, RADII } from '../../constants/theme.js';

const MODE_LABEL = { close: 'purge.company.modeClose', erase: 'purge.company.modeErase' };
const MODE_HELP = { close: 'purge.company.modeCloseHelp', erase: 'purge.company.modeEraseHelp' };

export default function CompanyPurgeDialog({ open, customerId, name, tenantNames = [], resume = false, onPurged, onClose }) {
  const { t } = useTranslation('admin');
  const [chosen, setChosen] = useState(null);
  const goneRef = useRef(false);

  useEffect(() => {
    if (open) { setChosen(null); goneRef.current = false; }
  }, [open]);

  const probe = usePurgePreview(open, () => previewCompanyPurge(customerId, 'erase'));
  let mode = chosen;
  if (resume) mode = probe.preview ? startedMode(probe.preview) : null;
  const closePreview = usePurgePreview(open && mode === 'close', () => previewCompanyPurge(customerId, 'close'));
  let current = null;
  if (mode === 'erase') current = probe;
  else if (mode === 'close') current = closePreview;

  const eraseOffKey = eraseUnavailableKey(probe.preview);
  const eraseDisabled = probe.loading || Boolean(eraseOffKey);

  let blockedText = null;
  const loadError = probe.error || current?.error;
  if (loadError) {
    blockedText = adminErrorText(t, loadError, { purge: true });
  } else if (current?.preview) {
    const blocked = companyBlocker(current.preview);
    if (blocked?.key) blockedText = t(blocked.key);
    else if (blocked?.code === 'TENANT_PROTECTED') blockedText = t('purge.company.tenantProtected');
    else if (blocked?.code) blockedText = adminErrorText(t, { code: blocked.code });
  }

  // C6 — no status (network) or a gateway timeout: the purge may still be running or done.
  function wordError(err) {
    const status = typeof err?.status === 'number' ? err.status : null;
    if (status === null || status === 504) return t('purge.company.uncertain');
    return adminErrorText(t, err, { purge: true });
  }

  async function handleConfirm() {
    try {
      const result = await purgeCompany(customerId, mode);
      onPurged(result);
    } catch (err) {
      if (isGoneError(err)) goneRef.current = true;
      throw err; // TypedConfirmDialog words it with wordError
    }
  }

  function handleClose() {
    onClose({ gone: goneRef.current || isGoneError(probe.error) });
  }

  function retry() {
    probe.reload();
    closePreview.reload();
  }

  const header = resume ? null : (
    <fieldset style={{ border: 'none', margin: '0 0 12px', padding: 0, fontFamily: FONT_SANS }}>
      <legend style={{ fontSize: '13px', fontWeight: 600, color: COLORS.ink, marginBottom: '8px' }}>
        {t('purge.company.modeLabel')}
      </legend>
      {PURGE_MODES.map((m) => {
        const disabled = m === 'erase' && eraseDisabled;
        const help = m === 'erase' && eraseOffKey ? eraseOffKey : MODE_HELP[m];
        return (
          <label
            key={m}
            style={{
              display: 'flex', gap: '8px', alignItems: 'flex-start', padding: '8px 10px', marginBottom: '6px',
              borderRadius: RADII.controlInner, border: `1px solid ${chosen === m ? COLORS.accent : COLORS.border}`,
              opacity: disabled ? 0.6 : 1, cursor: disabled ? 'not-allowed' : 'pointer',
            }}
          >
            <input
              type="radio"
              name="company-purge-mode"
              value={m}
              checked={chosen === m}
              disabled={disabled}
              onChange={() => setChosen(m)}
            />
            <span>
              <span style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: COLORS.ink }}>{t(MODE_LABEL[m])}</span>
              <span style={{ display: 'block', fontSize: '12px', color: COLORS.textSecondary }}>{t(help)}</span>
            </span>
          </label>
        );
      })}
    </fieldset>
  );

  let body = null;
  if (!mode) {
    body = resume ? null : (
      <p style={{ color: COLORS.textSecondary, fontSize: '13px', fontFamily: FONT_SANS }}>{t('purge.company.chooseMode')}</p>
    );
  } else if (current?.preview) {
    body = <CompanyPurgeSummary preview={current.preview} mode={mode} tenantNames={tenantNames} />;
  }

  return (
    <TypedConfirmDialog
      open={open}
      title={t('purge.company.title', { name })}
      header={header}
      expected={mode ? customerId : null}
      caseInsensitive
      sendValue={customerId}
      resetKey={mode}
      inputLabel={t('purge.company.typeLabel', { id: customerId })}
      confirmLabel={t('purge.company.confirm')}
      loading={resume ? probe.loading || Boolean(current?.loading) : Boolean(current?.loading)}
      blockedText={blockedText}
      onRetry={retry}
      busyText={t('purge.company.busy')}
      wordError={wordError}
      onConfirm={handleConfirm}
      onClose={handleClose}
    >
      {body}
    </TypedConfirmDialog>
  );
}
